<template>
  <ContentWrap>
    <el-form :inline="true" :model="queryParams">
      <el-form-item label="订单号">
        <el-input
          v-model="queryParams.orderNo"
          class="!w-220px"
          clearable
          placeholder="订单流水号"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" class="!w-140px" clearable placeholder="全部">
          <el-option label="申请中" :value="INVOICE_STATUS.PENDING" />
          <el-option label="已开具" :value="INVOICE_STATUS.ISSUED" />
          <el-option label="已驳回" :value="INVOICE_STATUS.REJECTED" />
          <el-option label="已取消" :value="INVOICE_STATUS.CANCELLED" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" />搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" />重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column label="订单号" prop="orderNo" min-width="160" />
      <el-table-column label="金额" width="100">
        <template #default="{ row }">¥{{ ((row.price || 0) / 100).toFixed(2) }}</template>
      </el-table-column>
      <el-table-column label="抬头" prop="title" min-width="140" show-overflow-tooltip />
      <el-table-column label="邮箱" prop="email" min-width="160" show-overflow-tooltip />
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.status)">{{ statusLabel(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="申请时间" prop="createTime" width="170" :formatter="dateFormatter" />
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row)">详情</el-button>
          <el-button
            v-if="row.status === INVOICE_STATUS.PENDING"
            link
            type="success"
            @click="openIssue(row)"
          >
            开具
          </el-button>
          <el-button
            v-if="row.status === INVOICE_STATUS.PENDING"
            link
            type="danger"
            @click="openReject(row)"
          >
            驳回
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <el-dialog v-model="detailVisible" title="发票详情" width="560px">
    <el-descriptions v-if="current" :column="1" border>
      <el-descriptions-item label="订单号">{{ current.orderNo }}</el-descriptions-item>
      <el-descriptions-item label="抬头">{{ current.title }}</el-descriptions-item>
      <el-descriptions-item label="税号">{{ current.taxNo || '—' }}</el-descriptions-item>
      <el-descriptions-item label="邮箱">{{ current.email }}</el-descriptions-item>
      <el-descriptions-item label="发票号">{{ current.invoiceNo || '—' }}</el-descriptions-item>
      <el-descriptions-item label="附件">
        <el-link v-if="current.invoiceUrl" :href="current.invoiceUrl" target="_blank" type="primary">
          打开
        </el-link>
        <span v-else>—</span>
      </el-descriptions-item>
      <el-descriptions-item label="备注">{{ current.adminRemark || '—' }}</el-descriptions-item>
      <el-descriptions-item label="驳回原因">{{ current.rejectReason || '—' }}</el-descriptions-item>
    </el-descriptions>
  </el-dialog>

  <el-dialog v-model="issueVisible" title="开具发票" width="480px">
    <el-form label-width="90px">
      <el-form-item label="发票号码">
        <el-input v-model="issueForm.invoiceNo" placeholder="选填" />
      </el-form-item>
      <el-form-item label="发票附件">
        <UploadFile
          v-model="issueForm.invoiceUrl"
          :file-type="['pdf', 'png', 'jpg', 'jpeg']"
          :file-size="20"
          :limit="1"
          :is-show-tip="true"
          :use-material-library="false"
          :replace-on-exceed="true"
          directory="mall/invoice"
        />
      </el-form-item>
      <el-form-item label="备注">
        <el-input v-model="issueForm.adminRemark" type="textarea" placeholder="选填" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="issueVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="submitIssue">确认开具</el-button>
    </template>
  </el-dialog>

  <el-dialog v-model="rejectVisible" title="驳回开票" width="480px">
    <el-input v-model="rejectReason" :rows="3" type="textarea" placeholder="驳回原因" />
    <template #footer>
      <el-button @click="rejectVisible = false">取消</el-button>
      <el-button type="danger" :loading="submitting" @click="submitReject">确认驳回</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import * as InvoiceApi from '@/api/merchant/invoice'
import { INVOICE_STATUS } from '@/api/merchant/invoice'
import { dateFormatter } from '@/utils/formatTime'
import { UploadFile } from '@/components/UploadFile'

defineOptions({ name: 'MerchantFinanceInvoice' })

const route = useRoute()
const message = useMessage()
const loading = ref(false)
const submitting = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  orderNo: '',
  orderId: undefined as number | undefined,
  status: undefined as number | undefined
})

const current = ref<any>(null)
const detailVisible = ref(false)
const issueVisible = ref(false)
const rejectVisible = ref(false)
const rejectReason = ref('')
const issueForm = reactive({ invoiceNo: '', invoiceUrl: '', adminRemark: '' })

const statusLabel = (status?: number) => {
  if (status === INVOICE_STATUS.PENDING) return '申请中'
  if (status === INVOICE_STATUS.ISSUED) return '已开具'
  if (status === INVOICE_STATUS.REJECTED) return '已驳回'
  if (status === INVOICE_STATUS.CANCELLED) return '已取消'
  return '—'
}

const statusTag = (status?: number) => {
  if (status === INVOICE_STATUS.PENDING) return 'warning'
  if (status === INVOICE_STATUS.ISSUED) return 'success'
  if (status === INVOICE_STATUS.REJECTED) return 'danger'
  return 'info'
}

const getList = async () => {
  loading.value = true
  try {
    const data = await InvoiceApi.getInvoicePage(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.orderNo = ''
  queryParams.orderId = undefined
  queryParams.status = undefined
  handleQuery()
}

const openDetail = (row: any) => {
  current.value = row
  detailVisible.value = true
}

const openIssue = (row: any) => {
  current.value = row
  issueForm.invoiceNo = ''
  issueForm.invoiceUrl = ''
  issueForm.adminRemark = ''
  issueVisible.value = true
}

const openReject = (row: any) => {
  current.value = row
  rejectReason.value = ''
  rejectVisible.value = true
}

const submitIssue = async () => {
  submitting.value = true
  try {
    await InvoiceApi.issueInvoice({ id: current.value.id, ...issueForm })
    message.success('已开具')
    issueVisible.value = false
    await getList()
  } finally {
    submitting.value = false
  }
}

const submitReject = async () => {
  if (!rejectReason.value.trim()) {
    message.warning('请填写驳回原因')
    return
  }
  submitting.value = true
  try {
    await InvoiceApi.rejectInvoice({ id: current.value.id, rejectReason: rejectReason.value.trim() })
    message.success('已驳回')
    rejectVisible.value = false
    await getList()
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  const orderId = Number(route.query.orderId)
  if (orderId) {
    queryParams.orderId = orderId
  }
  getList()
})
</script>
